import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, Send, FileText, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobsPage() {
  const { lang } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', cv_url: '', cv_filename: '' });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs`).then(r => setJobs(r.data)).catch(() => {});
  }, []);

  const openApply = (job) => {
    setSelectedJob(job);
    setForm({ name: '', email: '', phone: '', message: '', cv_url: '', cv_filename: '' });
    setOpen(true);
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(lang === 'fr' ? 'Fichier trop grand (max 10MB)' : 'File too large (max 10MB)');
      return;
    }
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload/cv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm(f => ({ ...f, cv_url: fullUrl, cv_filename: data.filename || file.name }));
      toast.success(lang === 'fr' ? 'CV téléversé!' : 'CV uploaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur de téléversement' : 'Upload error'));
    } finally {
      setUploadingCv(false);
    }
  };

  const removeCv = () => setForm(f => ({ ...f, cv_url: '', cv_filename: '' }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(lang === 'fr' ? 'Nom et courriel requis' : 'Name and email required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/apply`, {
        job_id: selectedJob?.id || '',
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        cv_url: form.cv_url,
        cv_filename: form.cv_filename,
      });
      toast.success(lang === 'fr' ? 'Candidature envoyée! Merci.' : 'Application sent! Thank you.');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="jobs-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight"
          >
            {lang === 'fr' ? 'Joignez-vous à notre équipe' : 'Join our team'}
          </motion.h1>
          <p className="text-[#555555] mt-3 text-base">
            {lang === 'fr' ? 'Découvrez nos postes ouverts et postulez en ligne' : 'Discover our open positions and apply online'}
          </p>
          <div className="w-16 h-1 mx-auto mt-4 bg-[#e53a3a]" />
        </div>

        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-[#bbb] mb-4" />
            <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-2">
              {lang === 'fr' ? 'Aucun poste ouvert pour le moment' : 'No open positions at the moment'}
            </h3>
            <p className="text-sm text-[#555555] mb-6">
              {lang === 'fr' ? 'Vous pouvez tout de même nous faire parvenir votre candidature spontanée.' : 'You can still send us a spontaneous application.'}
            </p>
            <button
              onClick={() => openApply(null)}
              className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white bg-[#e53a3a] hover:bg-[#c62828]"
              data-testid="spontaneous-apply-btn"
            >
              <Send className="w-4 h-4" /> {lang === 'fr' ? 'Candidature spontanée' : 'Spontaneous application'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl border border-[#e0e0e0] p-6 hover:shadow-lg transition-shadow"
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-2">
                      {lang === 'fr' ? job.title_fr : job.title_en}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-[#555555] mb-3 flex-wrap">
                      {(job.type_fr || job.type_en) && (
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {lang === 'fr' ? job.type_fr : job.type_en}</span>
                      )}
                      {(job.location_fr || job.location_en) && (
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {lang === 'fr' ? job.location_fr : job.location_en}</span>
                      )}
                    </div>
                    <p className="text-sm text-[#555555] whitespace-pre-line">{lang === 'fr' ? job.description_fr : job.description_en}</p>
                  </div>
                  <button
                    onClick={() => openApply(job)}
                    className="btn-belgo shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-white bg-[#e53a3a] hover:bg-[#c62828]"
                    data-testid={`apply-btn-${job.id}`}
                  >
                    <Send className="w-4 h-4" /> {lang === 'fr' ? 'Postuler' : 'Apply'}
                  </button>
                </div>
              </motion.div>
            ))}
            <div className="text-center pt-6">
              <button
                onClick={() => openApply(null)}
                className="text-sm text-[#e53a3a] hover:underline font-semibold"
                data-testid="spontaneous-apply-link"
              >
                {lang === 'fr' ? 'Ou envoyez une candidature spontanée →' : 'Or send a spontaneous application →'}
              </button>
            </div>
          </div>
        )}

        {/* Apply dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {lang === 'fr' ? 'Postuler' : 'Apply'}
                {selectedJob ? ` — ${lang === 'fr' ? selectedJob.title_fr : selectedJob.title_en}` : ` — ${lang === 'fr' ? 'Candidature spontanée' : 'Spontaneous application'}`}
              </DialogTitle>
              <DialogDescription>
                {lang === 'fr' ? 'Remplissez ce formulaire pour soumettre votre candidature.' : 'Fill out this form to submit your application.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label>{lang === 'fr' ? 'Nom complet' : 'Full name'} *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="apply-name" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Courriel' : 'Email'} *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="apply-email" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                <Input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" data-testid="apply-phone" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Message / Expérience' : 'Message / Experience'}</Label>
                <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={5} className="mt-1" data-testid="apply-message" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'CV (PDF, DOC, image — max 10MB)' : 'CV (PDF, DOC, image — max 10MB)'}</Label>
                {form.cv_url ? (
                  <div className="mt-1 flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-green-300 bg-green-50">
                    <span className="flex items-center gap-2 text-sm text-green-800 truncate">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="truncate">{form.cv_filename || 'CV.pdf'}</span>
                    </span>
                    <button type="button" onClick={removeCv} className="text-red-600 hover:bg-red-100 p-1 rounded shrink-0" data-testid="apply-cv-remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-1">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleCvUpload}
                      disabled={uploadingCv}
                      className="block w-full text-sm text-[#555555] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#e53a3a] file:text-white hover:file:bg-[#c62828] file:cursor-pointer"
                      data-testid="apply-cv-file"
                    />
                    {uploadingCv && <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Téléversement...' : 'Uploading...'}</p>}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#e53a3a' }}
                data-testid="apply-submit"
              >
                {submitting ? '...' : (lang === 'fr' ? 'Envoyer ma candidature' : 'Send my application')}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
